import { useState, useEffect, useRef } from "react"; 
import { useLocation, useNavigate } from "react-router-dom";
import { 
  CalendarDays, 
  Search, 
  Plus, 
  Check, 
  X, 
  Clock, 
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import api from "../../connector/api";
import Header from "../../components/Header";
import LeaveFormModal from "./LeaveFormModal";
import CreateLeaveModal from "./CreateLeaveModal";

export default function UnifiedLeaveDashboard() {
  const [user, setUser] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Controls, Filters & Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; 

  const location = useLocation();
  const [focusedReviewLeave, setFocusedReviewLeave] = useState(null);
  const navigate = useNavigate();

  // ⚡ MUTABLE STORAGE REFS: Prevents stale render closure traps during async streams
  const userPermissionsRef = useRef([]);
  const userRecordRef = useRef(null);

  // 1. Target background fetcher (Runs cleanly when backend notification stream alerts changes)
  const fetchLeavesOnly = async (isAdminOrManager) => {
    try {
      // ⚡ FIX: Fallback directly to the live mutable ref pointer to avoid stale render state
      const activeIsAdmin = isAdminOrManager ?? (
        userPermissionsRef.current.includes("can_approve_leave") || 
        userPermissionsRef.current.includes("can_view_all_leaves") ||
        userPermissionsRef.current.includes("can_view_leave")
      );

      if (activeIsAdmin) {
        const res = await api.get("/leaves/");
        const dataPayload = Array.isArray(res.data) ? res.data : res.data.results || [];
        setLeaves(dataPayload);
      } else {
        const myRes = await api.get("leaves/my-requests/");
        const dataPayload = Array.isArray(myRes.data) ? myRes.data : myRes.data.results || [];
        setLeaves(dataPayload);
      }
    } catch (err) {
      console.error("Event-driven sync failed gathering leave payloads:", err);
    }
  };

  //  CONSOLIDATED INITIALIZATION & LIVE BROADCAST SUBSCRIPTION ENGINE
  useEffect(() => {
    // 1. Initial full engine configuration loader
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const userRes = await api.get("/accounts/me/");
        setUser(userRes.data);
        
        // Synchronize latest values straight into mutable ref containers immediately
        userRecordRef.current = userRes.data;
        const userPermissions = userRes.data?.permissions || [];
        userPermissionsRef.current = userPermissions;

        const isManagerUser = userPermissions.includes("can_approve_leave");
        const isSuperAdmin = userPermissions.includes("can_view_all_leaves");
        const isGlobalViewer = userPermissions.includes("can_view_leave");

        // Fetch initial leaves right away
        await fetchLeavesOnly(isSuperAdmin || isManagerUser || isGlobalViewer);
      } catch (err) {
        console.error("Dashboard engine failed to load configuration:", err);
      } finally {
        setLoading(false);
      }
    };

    // 2. Realtime Event Listener Trigger Core
    const handleSystemSync = () => {
      console.log("⚡ Live WebSocket ping caught! Auto-refreshing directory registry data...");
      
      // Always calculate permissions directly from the mutable reference pointer inside the trigger event
      const isManagerUser = userPermissionsRef.current.includes("can_approve_leave");
      const isSuperAdmin = userPermissionsRef.current.includes("can_view_all_leaves");
      const isGlobalViewer = userPermissionsRef.current.includes("can_view_leave");
      
      fetchLeavesOnly(isSuperAdmin || isManagerUser || isGlobalViewer);
    };

    // Fire dashboard payload config load
    fetchDashboardData();

    // Bind active network system triggers
    window.addEventListener("refreshLeaveDashboard", handleSystemSync);
    window.addEventListener("localLeaveUpdate", handleSystemSync);
    
    return () => {
      window.removeEventListener("refreshLeaveDashboard", handleSystemSync);
      window.removeEventListener("localLeaveUpdate", handleSystemSync);
    };
  }, []); // Runs cleanly once, but accesses fresh references inside listeners

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Dispatches local updates to make sure UI indices reflect data updates cleanly
  const handleLeaveAction = async (id, actionType) => {
    try {
      await api.post(`/leaves/${id}/${actionType}/`);
      const statusValue = actionType === "approve" ? "APPROVED" : "REJECTED";

      setLeaves(prev => prev.map(leave => leave.id === id ? { ...leave, status: statusValue } : leave));
      setFocusedReviewLeave(prev => prev && prev.id === id ? { ...prev, status: statusValue } : prev);
      
      // Trigger update event to realign dashboard metrics and secondary layers instantly
      window.dispatchEvent(new Event("localLeaveUpdate"));
      
      navigate("/leave", { replace: true });
    } catch (err) {
      console.error(`Error processing leave status update for record ${id}:`, err);
    }
  };

  const handleLeaveSubmitted = () => {
    window.dispatchEvent(new Event("localLeaveUpdate"));
  };

  const isManager = (
    user?.permissions?.includes("can_approve_leave") || 
    user?.permissions?.includes("can_view_all_leaves")
  );

  // Filter against master list seamlessly
  const filteredLeaves = leaves
    .filter(l => statusFilter === "ALL" || l.status === statusFilter)
    .filter(l => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        (l.employee_name || "").toLowerCase().includes(query) ||
        (l.leave_type || "").toLowerCase().includes(query)
      );
    });

  const totalPages = Math.ceil(filteredLeaves.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const displayedLeaves = filteredLeaves.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const requestIdToOpen = queryParams.get("openRequest");

    if (requestIdToOpen && leaves.length > 0) {
      const matchedRecord = leaves.find(l => String(l.id) === String(requestIdToOpen));
      if (matchedRecord) {
        setFocusedReviewLeave(matchedRecord);
      }
    }
  }, [location.search, leaves]);

  const handleCloseReviewModal = () => {
    setFocusedReviewLeave(null);
    navigate("/leave", { replace: true });
  };

  const totalPendingCount = leaves.filter(t => t.status === "PENDING").length;
  const totalApprovedCount = leaves.filter(t => t.status === "APPROVED").length;

  return (
    <div className="min-h-screen bg-[#fafafa] font-dynamic-title text-zinc-900 antialiased selection:bg-zinc-900/5">
      <div className="border-b border-zinc-200/80 bg-white sticky top-0 z-40 backdrop-blur-md bg-white/80">
        <Header />
      </div>

      <main className="max-w-7xl w-full mx-auto px-6 pt-5 pb-10 space-y-8">
        
        {/* Page Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="text-[8px] font-bold text-[#002855] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/40">
              Core HR Administration
            </span>
            <h1 className="text-lg font-extrabold text-slate-900 tracking-tight mt-1.5">Leave Directory</h1>
            <p className="text-xs text-slate-500 mt-0.5">Oversee complete administrative absence records and track organizational data.</p>
          </div>

          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex-1 md:flex-initial h-9 px-4 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white text-xs font-bold rounded-xl transition shadow-xs flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            <Plus className="w-4 h-4" />
            <span>Create Leave</span>
          </button>
        </div>

        {/* Global Metric Counters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: "Pending Review", count: totalPendingCount, suffix: "requests", icon: <Clock size={16} />, color: "bg-amber-50/60 text-amber-600 border-amber-100/50" },
            { label: "Approved Absences", count: totalApprovedCount, suffix: "granted", icon: <Check size={16} />, color: "bg-emerald-50/60 text-emerald-600 border-emerald-100/50" },
            { label: "Total Logged Registry", count: leaves.length, suffix: "submissions", icon: <Briefcase size={16} />, color: "bg-zinc-50 text-zinc-600 border-zinc-200/40" }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white p-3 border border-zinc-200/60 rounded-xl shadow-xs flex items-center gap-3">
              <div className={`p-2 rounded-lg border ${stat.color}`}>{stat.icon}</div>
              <div>
                <span className="block text-[9px] font-medium text-zinc-400 tracking-wide">{stat.label}</span>
                <span className="text-sm font-semibold text-zinc-800 block mt-0.5">
                  {stat.count} <span className="text-[10px] font-normal text-zinc-400">{stat.suffix}</span>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Filters and Search Strip */}
        <div className="flex flex-col lg:flex-row justify-between gap-4 items-stretch lg:items-center bg-white p-2 border border-zinc-200/60 rounded-xl shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-1 overflow-x-auto p-1 bg-zinc-50 rounded-lg border border-zinc-200/40">
              {["ALL", "PENDING", "APPROVED", "REJECTED"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
                    statusFilter === status
                      ? "bg-white text-zinc-900 border border-zinc-200/80 shadow-xs"
                      : "text-zinc-400 hover:text-zinc-700"
                  }`}
                >
                  {status === "ALL" ? "All Status" : status.charAt(0) + status.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="relative lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name or leave type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 focus:ring-0 rounded-lg pl-9 pr-4 text-xs font-medium outline-none transition-all"
            />
          </div>
        </div>

        {/* Data Table Container */}
        <div className="bg-white border border-zinc-200/70 rounded-xl shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-50/70 border-b border-zinc-100 text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
                  <th className="py-4 px-4 w-20">ID</th>
                  <th className="py-4 px-4">Employee</th>
                  <th className="py-4 px-4">Type</th>
                  <th className="py-4 px-4">Timeline Scope</th>
                  <th className="py-4 px-4 max-w-xs">Reason/Context</th>
                  <th className="py-4 px-4 text-center w-28">Status</th>
                  <th className="py-4 px-4 text-right pr-6 w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 text-xs text-zinc-600 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-16 text-center text-zinc-400 font-medium">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full border-2 border-zinc-300 border-t-zinc-800 animate-spin" />
                        <span>Streaming application logs...</span>
                      </div>
                    </td>
                  </tr>
                ) : displayedLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-16 text-center text-zinc-400 font-medium">
                      No matching leave profiles found.
                    </td>
                  </tr>
                ) : (
                  displayedLeaves.map((leave) => {
                    const badgeStyles = {
                      PENDING: "bg-amber-50 text-amber-700 border-amber-200/40 status-dot-amber",
                      APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200/40 status-dot-emerald",
                      REJECTED: "bg-zinc-100 text-zinc-600 border-zinc-200/60 status-dot-zinc"
                    }[leave.status] || "bg-zinc-50 text-zinc-600 border-zinc-200";

                    return (
                      <tr 
                        key={leave.id} 
                        className="hover:bg-zinc-50/50 transition-colors cursor-pointer group"
                        onClick={() => setFocusedReviewLeave(leave)}
                      >
                        <td className="py-4 px-4 font-mono text-zinc-400 text-[11px]">#{leave.id}</td>
                        <td className="py-4 px-4 font-semibold text-zinc-900 group-hover:text-zinc-700 transition-colors">
                          {leave.employee_name || user?.full_name}
                        </td>
                        <td className="py-4 px-4">
                          <span className="inline-flex items-center bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded text-[10px] font-medium text-zinc-600">
                            {leave.leave_type}
                          </span>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono">
                            <span>{leave.start_date}</span>
                            <span className="text-zinc-300">&rarr;</span>
                            <span>{leave.end_date}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 max-w-xs truncate text-zinc-400 font-normal italic">
                          {leave.reason || "No documentation supplied."}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-medium tracking-wide ${badgeStyles}`}>
                            {leave.status.toLowerCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right pr-6" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setFocusedReviewLeave(leave)}
                              className="h-7 w-7 rounded-md border border-zinc-200 bg-white text-zinc-400 hover:text-zinc-700 hover:border-zinc-300 flex items-center justify-center transition-all shadow-2xs"
                              title="Inspect File"
                            >
                              <Eye size={13} />
                            </button>
                            {isManager && leave.status === "PENDING" && (
                              <>
                                <button
                                  onClick={() => handleLeaveAction(leave.id, "approve")}
                                  className="h-7 w-7 rounded-md bg-white border border-green-500 text-green-500 hover:border-green-500 flex items-center justify-center transition-all shadow-2xs"
                                  title="Approve"
                                >
                                  <Check size={13} className="stroke-[2.5]" />
                                </button>
                                <button
                                  onClick={() => handleLeaveAction(leave.id, "reject")}
                                  className="h-7 w-7 rounded-md border border-red-200 bg-white text-rose-500 hover:text-rose-600 hover:border-rose-200 flex items-center justify-center transition-all shadow-2xs"
                                  title="Reject"
                                >
                                  <X size={13} className="stroke-[2.5]" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Pagination */}
          <div className="bg-zinc-50/70 border-t border-zinc-100 p-4 px-6 flex items-center justify-between">
            <span className="text-xs text-zinc-400 font-medium">
              Showing <span className="font-semibold text-zinc-700">{filteredLeaves.length > 0 ? indexOfFirstItem + 1 : 0}</span> to <span className="font-semibold text-zinc-700">{Math.min(indexOfLastItem, filteredLeaves.length)}</span> of <span className="font-semibold text-zinc-700">{filteredLeaves.length}</span> records
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                className="h-8 w-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-500 hover:bg-zinc-50 transition disabled:opacity-40 disabled:hover:bg-white shadow-2xs"
              >
                <ChevronLeft size={15} />
              </button>
              <div className="text-xs font-medium text-zinc-500 px-2">
                Page {currentPage} of {totalPages}
              </div>
              <button
                type="button"
                disabled={currentPage === totalPages || loading}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                className="h-8 w-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-500 hover:bg-zinc-50 transition disabled:opacity-40 disabled:hover:bg-white shadow-2xs"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </main>

      <LeaveFormModal isOpen={isSubmitModalOpen} onClose={() => setIsSubmitModalOpen(false)} onLeaveSubmitted={handleLeaveSubmitted} />
      <CreateLeaveModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onLeaveCreated={handleLeaveSubmitted} />

      {/* Side-Modal Review Panel */}
      {focusedReviewLeave && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-[6px] flex items-center justify-center p-4 transition-all">
          <div className="bg-white rounded-xl max-w-md w-full border border-zinc-200/80 shadow-2xl overflow-hidden p-6 space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider border border-zinc-200/60">
                  Request ID: #{focusedReviewLeave.id}
                </span>
                <h2 className="text-lg font-bold text-zinc-900 tracking-tight pt-1">
                  {focusedReviewLeave.employee_name || "Personal Request"}
                </h2>
              </div>
              <button 
                onClick={handleCloseReviewModal}
                className="text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 h-8 w-8 flex items-center justify-center rounded-xl border border-zinc-200/80 bg-white transition-all shadow-xs"
              >
                <X size={15} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-50/50 border border-zinc-200/60 p-3.5 rounded-xl">
                  <span className="block text-[10px] text-zinc-400 font-medium uppercase tracking-wider">Leave Category</span>
                  <span className="font-semibold text-zinc-800 text-xs mt-1 block">{focusedReviewLeave.leave_type}</span>
                </div>
                
                <div className="bg-zinc-50/50 border border-zinc-200/60 p-3.5 rounded-xl flex flex-col justify-between">
                  <span className="block text-[10px] text-zinc-400 font-medium uppercase tracking-wider">Current Status</span>
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide border mt-1 uppercase ${
                      {
                        PENDING: "bg-amber-50 text-amber-700 border-amber-200/60",
                        APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200/60",
                        REJECTED: "bg-rose-50 text-rose-700 border-rose-200/60"
                      }[focusedReviewLeave.status] || "bg-zinc-50 text-zinc-600 border-zinc-200"
                    }`}>
                      {focusedReviewLeave.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-50/50 border border-zinc-200/60 p-3.5 rounded-xl space-y-1.5">
                <span className="block text-[10px] text-zinc-400 font-medium uppercase tracking-wider">Duration Scope</span>
                <div className="flex items-center gap-2 font-mono text-xs font-semibold text-zinc-700">
                  <CalendarDays size={14} className="text-zinc-400" />
                  <span>{focusedReviewLeave.start_date}</span>
                  <span className="text-zinc-300 font-normal">&rarr;</span>
                  <span>{focusedReviewLeave.end_date}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="block text-[10px] text-zinc-400 font-medium uppercase tracking-wider">Absence Reason Context</span>
                <div className="bg-zinc-50/30 p-4 rounded-xl border border-zinc-200/50 text-zinc-600 leading-relaxed text-xs font-normal relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-zinc-200" />
                  <p className="italic pl-1">
                    "{focusedReviewLeave.reason || "No explicit notes provided by employee."}"
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
              <button
                onClick={handleCloseReviewModal}
                className="h-9 px-4 text-xs font-medium border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 rounded-xl transition-all shadow-xs"
              >
                <X size={15} />
              </button>
            </div>

            <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
              <button
                onClick={handleCloseReviewModal}
                className="h-9 px-4 text-xs font-medium border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 rounded-xl transition-all shadow-xs"
              >
                Dismiss
              </button>

              {focusedReviewLeave.status === "PENDING" && isManager && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleLeaveAction(focusedReviewLeave.id, "reject")}
                    className="h-9 px-4 border border-rose-200 bg-white hover:bg-rose-50/50 text-rose-600 font-semibold text-xs rounded-xl transition-all shadow-xs"
                  >
                    Reject Request
                  </button>
                  <button
                    onClick={() => handleLeaveAction(focusedReviewLeave.id, "approve")}
                    className="h-9 px-4 border border-green-900 text-green-900 font-semibold text-xs rounded-xl transition-all shadow-md active:scale-[0.98]"
                  >
                    Approve Entry
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}